import React, { createContext, useContext, useState } from 'react';
import PropTypes from 'prop-types';

// Create context
const OrderConfirmationContext = createContext();

/**
 * Provider component for the order confirmation modal
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Provider component
 */
export const OrderConfirmationProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [orderingUrl, setOrderingUrl] = useState(null);
  const [locationName, setLocationName] = useState(null);

  // Function to open the confirmation modal
  const confirmOrder = (url, location) => {
    setOrderingUrl(url);
    setLocationName(location);
    setIsOpen(true);
  };

  // Function to close the confirmation modal
  const closeConfirmation = () => {
    setIsOpen(false);
    // Clear data after animation
    setTimeout(() => {
      setOrderingUrl(null);
      setLocationName(null);
    }, 300);
  };

  // Function to proceed with the order
  const proceedToOrder = () => {
    if (orderingUrl) {
      window.open(orderingUrl, '_blank', 'noopener,noreferrer');
    }
    closeConfirmation();
  };

  return (
    <OrderConfirmationContext.Provider
      value={{
        isOpen,
        orderingUrl,
        locationName,
        confirmOrder,
        closeConfirmation,
        proceedToOrder
      }}
    >
      {children}
    </OrderConfirmationContext.Provider>
  );
};

OrderConfirmationProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Custom hook to use the order confirmation context
 * @returns {Object} Order confirmation context
 */
export const useOrderConfirmation = () => {
  const context = useContext(OrderConfirmationContext);
  if (!context) {
    throw new Error('useOrderConfirmation must be used within an OrderConfirmationProvider');
  }
  return context;
};
